import { ChangeDetectorRef, Component, effect, ElementRef, Input, QueryList, ViewChild, ViewChildren, viewChild, WritableSignal, signal, OnInit, OnDestroy } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router } from '@angular/router';
import { ReadMoreComponent } from '@components/read-more/read-more.component';
import { ApiClientConstant } from '@cureskin/api-client';
import { EntityEnum } from '@cureskin/api-client-rn';
import { InstantCheckup } from '@cureskin/api-client/src/server-api';
import { AiAssistantService } from '@services/ai-assistant-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { CurrentComponentService } from '@services/current-component';
import { EventLoggerService } from '@services/event-logger-service';
import { PRIMARY_CHANNELS } from '@services/event-logger-service/event-logger.constant';
import { ConsultationSessionService } from '@services/consultation-session-service';
import { shouldAnswerPhotoQuestion } from '@services/consultation-quiz-engine-service/consultation-quiz-engine.types';
import { ExperimentService } from '@services/experiment/experiment.service';
import { FollowUpService } from '@services/followup-service/followup.service';
import { ConcernDetectionsService } from '@services/concern-detections-service';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { buildExtraData, CaptureContext, CaptureIntent, mergeContext, parseContext, PhotoCaptureService } from '@services/photo-capture-service';
import { LocalStorageService } from '@services/local-storage-service';
import { ReAcquisitionService } from '@services/re-acquisition-service/re-acquisition.service';
import { SelfCheckupService } from '@services/self-checkup-service';
import { UserService } from '@services/user/user.service';
import { WindowRefService } from '@services/window-ref-service';
import { BothSideFaceBottomSheetComponent } from '@shared/bottom-sheet-layouts/both-side-face-bottom-sheet/both-side-face-bottom-sheet.component';
import { DeletePhotoSheetComponent } from '@shared/bottom-sheet-layouts/delete-photo-sheet/delete-photo-sheet.component';
import { MoreInsightsBottomsheetComponent} from '@shared/bottom-sheet-layouts/more-insights-bottomsheet/more-insights-bottomsheet.component';
import { SideFacePhotoBottomSheetComponent } from '@shared/bottom-sheet-layouts/side-face-photo-bottom-sheet/side-face-photo-bottom-sheet.component';
import { WrongPhotoDetectedComponent } from '@shared/bottom-sheet-layouts/wrong-photo-detected/wrong-photo-detected.component';
import {
  FollowUpQuestionsSheetComponent,
  FollowUpQuestionsSheetData,
} from '@shared/follow-up-questions-sheet/follow-up-questions-sheet.component';
import { InstantCheckupStatusBannerStateEnum, HeaderTypeEnum, ButtonVariant, FooterLayout, FooterContainerVariant, I18nKey, InvalidPhotoUiCode, InvalidPhotoErrorMapping, InvalidPhotoErrorMappingVariant, UI_CODE_CATEGORY_MAP } from '@shared/types/instant-checkup.types';
import { AppConfig } from 'client/app/app.config';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { SwiperOptions } from 'swiper';
import { SwiperComponent } from 'swiper/angular';

import { AllocatedRegimen } from '../../../app-constant-types';
import { buildAiParameters, ParameterViewModel } from '../../../../helper/report-helper/report-helper';
import { problemsCopy, concernSeverityTranslations } from '../../problem-details';
import { AiDetectionResult, DisplayDetectionResult, HairFrontCardState, PolygonEntry, ScalpCardCopy, ScalpCardState, ScalpSeverity, DetectionViewMode, HAIRLINE_DETECTED_COLOR, HAIRLINE_RECESSION_CLASS, HAIRLINE_WHITE_COLOR, SCALP_SEGMENT_CLASS, TEAL_DETECTION_COLOR, FALLING_HAIR_COLOR } from './checkup-view-constant';
import { FACE_PROBLEM_CLASS_MAPPING } from './face-problem-class-mappings';
import { computeHairlineRecession } from './hairline-recession';


const INVALID_PHOTO_ATTEMPTS_KEY_PREFIX = 'invalidPhotoAttempts_';
const INVALID_PHOTO_ATTEMPTS_BEFORE_PROCEED = 2;

interface ConcernSeverity {
  'Mild': string;
  'Moderate': string;
  'Severe': string;
}

interface ConcernSeverityThreshold {
  [key: string]: {
    mild: number;
    moderate: number;
  };
}

interface Point {
  x: number;
  y: number;
}

/** The quiz question each hair capture answers. Types absent here never answer a quiz question. */
const PHOTO_QUESTION_ID_BY_CHECKUP_TYPE: Readonly<Record<string, string>> = {
  HAIR_FRONT: 'HairfallPhoto',
  HAIR_TOP: 'HairfallPhotoTop',
};
const PHOTO_REGIMEN_CLASS_BY_CHECKUP_TYPE: Readonly<Record<string, string>> = {
  HAIR_FRONT: 'HAIR',
  HAIR_TOP: 'HAIR',
};

interface AcceptedInvalidPhotoAnswerConfig {
  questionIds: readonly string[];
  regimenClass: string;
}

const ACCEPTED_INVALID_PHOTO_ANSWER_BY_TYPE: Readonly<Record<string, AcceptedInvalidPhotoAnswerConfig>> = {
  FACE: { questionIds: ['TakeInstantCheckup', 'TakeInstantCheckFrontFace'], regimenClass: 'FACE' },
  FRONT_FACE: { questionIds: ['TakeInstantCheckup', 'TakeInstantCheckFrontFace'], regimenClass: 'FACE' },
  FULL_FACE: { questionIds: ['TakeInstantCheckup', 'TakeInstantCheckFrontFace'], regimenClass: 'FACE' },
  LEFT_FACE: { questionIds: ['TakeInstantCheckSideFace'], regimenClass: 'FACE' },
  LEFT_SIDE_FACE: { questionIds: ['TakeInstantCheckSideFace'], regimenClass: 'FACE' },
  RIGHT_FACE: { questionIds: ['TakeInstantCheckSideFace'], regimenClass: 'FACE' },
  RIGHT_SIDE_FACE: { questionIds: ['TakeInstantCheckSideFace'], regimenClass: 'FACE' },
  SIDE_FACE: { questionIds: ['TakeInstantCheckSideFace'], regimenClass: 'FACE' },
  HAIR_FRONT: { questionIds: ['HairfallPhoto'], regimenClass: 'HAIR' },
  HAIR_TOP: { questionIds: ['HairfallPhotoTop'], regimenClass: 'HAIR' },
};

@Component({
  selector: 'app-checkup-view-experiment',
  templateUrl: './checkup-view-experiment.component.html',
  styles: [':host {@apply tw-flex tw-flex-col tw-h-full tw-bg-gray-100;}'],
  standalone: false,
})
export class CheckupViewExperimentComponent implements OnInit, OnDestroy {
  @Input() isFromListView: boolean = false;
  @Input('experiments') experiments: any[];
  @Input() instantCheckup: any;
  isDocumentRoute: boolean = false;
  @ViewChild('readMoreComponent', { static: true }) readMoreComponent: ReadMoreComponent;

  @ViewChildren(ReadMoreComponent) readMoreComponents!: QueryList<ReadMoreComponent>;
  @ViewChild('swiperRef', { static: false }) swiper: SwiperComponent;
  image: HTMLImageElement;
  faceLandmarks: any;
  ui: any = {};
  user: any;
  results: DisplayDetectionResult[] = [];
  croppingConfig: { top: number, left: number, ratio: number, offsetX?: number, offsetY?: number };
  detectionVerticalOffset: number | null = null;
  selectedDetection: DisplayDetectionResult | null;
  problemsCopy: { [key: string]: { [key: string]: { name: string, info: string } } } = problemsCopy;
  concernSeverityTranslations: { [key: string]: { [key: string]: string } } = concernSeverityTranslations;
  loading: boolean = true;
  tag: any;
  consultationSession: any;

  carouselConfig: SwiperOptions = {
    slidesPerView: 1.1,
    navigation: { prevEl: 'nonext', nextEl: 'nonext' },
    autoplay: false,
    height: 120,
  };

  concernSeverity: ConcernSeverity = {
    Mild: 'Mild',
    Moderate: 'Moderate',
    Severe: 'Severe',
  };
  concernSeverityThreshold: ConcernSeverityThreshold = {
    'pimple-region': {
      mild: 2,
      moderate: 5,
    },
    'darkspot-region': {
      mild: 4,
      moderate: 8,
    },
    'come-region': {
      mild: 4,
      moderate: 8,
    },
    'ascar-region': {
      mild: 2,
      moderate: 5,
    },
    PerioralPigmentation: { // Always severe for now
      mild: 0,
      moderate: 0,
    },
    OpenPores: { // Always Moderate for now
      mild: 0,
      moderate: 100,
    },
  };
  faceType: string;
  shouldShowIncorrectPhotoDetectedSheet: boolean;
  doesPhotoTypeMatches: boolean;
  instantCheckupIdFromRoute: any;
  subscriptions: Array<Subscription> = [];
  faceChatMainConcern: string;
  faceChatSecondaryConcerns: string[];
  isShowHairlineDetectionExperiment: boolean;
  isHairlineDetected: boolean;
  isIdealHairlineShown: boolean = false;
  isTempleBaldnessDetected: boolean;
  isFallingHair: boolean = false;
  expandedItems: boolean[] = [];
  summaryFullText: string = '';
  summaryExpanded: boolean = false;
  responsiveCardReadMoreExperiment: boolean = false;
  handleBlurPhotosExperiment: boolean = false;
  noBlurDetected: boolean;
  blurWithConcernsDetected: boolean;
  blurWithoutConcernsDetected: boolean;
  currentUser: any;
  instantCheckups: any;
  availableImagesArray: any[];
  skipLocalStorageDelete: boolean = false;
  private captureReturnSubscription?: Subscription;
  instantCheckupFromSupportChat: boolean = false;
  fromSelfCheckupBanner: boolean = false;
  today: Date = new Date();
  userCheckupImages: any;
  isReacquisitionUser: boolean = false;
  followUp: Table.FollowUp;
  excludeInstantCheckupProblems: string[] = [];
  private fupQuestionsSheetOpened: boolean = false;
  isHairTopType: boolean = false;
  isHairFrontType: boolean = false;
  isReclassifiedHairFront: boolean = false;
  showHairFrontFallbackCard: boolean = false;
  showHairTopWithScores: boolean = false;
  readonly hairAiDetectionTealColor = signal<boolean>(false);
  readonly showDetectionViewTab = signal<boolean>(false);
  detectionViewMode: WritableSignal<DetectionViewMode>;
  readonly detectionFilledPillRef = viewChild<ElementRef<HTMLElement>>('detectionFilledPill');
  readonly detectionOutlinedPillRef = viewChild<ElementRef<HTMLElement>>('detectionOutlinedPill');
  readonly detectionViewIndicatorLeft = signal<number>(0);
  readonly detectionViewIndicatorWidth = signal<number>(0);
  readonly DETECTION_VIEW_INDICATOR_TRANSITION_MS = 400;
  scalpDetectionSeverity: string;
  hairlineRecessionLobes: number[][][] = [];
  HairFrontCardState = HairFrontCardState;
  scalpParameters: ParameterViewModel[] = [];

  instantCheckupStatusBannerState: InstantCheckupStatusBannerStateEnum;
  InstantCheckupStatusBannerStateEnum = InstantCheckupStatusBannerStateEnum;
  isInvalidPhoto: boolean = false;
  canProceedWithInvalidPhoto: boolean = false;
  isFollowUpReportPending: boolean = false;
  invalidPhotoErrorMappingExperiment: InvalidPhotoErrorMappingVariant | null = null;
  resolvedInvalidPhotoUiCode: InvalidPhotoUiCode = InvalidPhotoUiCode.FALLBACK;
  HeaderTypeEnum = HeaderTypeEnum;
  ButtonVariant = ButtonVariant;
  FooterContainerVariant = FooterContainerVariant;
  I18nKey = I18nKey;
  private _footerLayout: FooterLayout = { buttons: [], containerVariant: FooterContainerVariant.DEFAULT };
  private _footerLayoutDependencies: string = '';

  constructor(
    private localStorageService: LocalStorageService,
    private connectionService: ConnectionService,
    private appWebBridge: AppWebBridgeService,
    private eventLogger: EventLoggerService,
    public windowRef: WindowRefService,
    public broadcast: BroadcastService,
    public route: ActivatedRoute,
    public appConfig: AppConfig,
    private router: Router,
    public bottomSheet: MatBottomSheet,
    public changeDetection: ChangeDetectorRef,
    private readonly instantCheckupService: InstantCheckupService,
    private readonly photoCapture: PhotoCaptureService,
    private readonly reAcquisitionService: ReAcquisitionService,
    private readonly currentComponent: CurrentComponentService,
    private readonly selfCheckupService: SelfCheckupService,
    private readonly followUpService: FollowUpService,
    public readonly experimentService: ExperimentService,
    private readonly concernDetectionsService: ConcernDetectionsService,
    private readonly aiAssistantService: AiAssistantService,
    private readonly consultationSessionService: ConsultationSessionService,
    private readonly userService: UserService,
  ) {
    this.currentComponent.set(this);
    this.detectionViewMode = signal<DetectionViewMode>(this.appConfig.Shared.InstantCheckup.Detections.View.FILLED);
    effect((): void => {
      const mode = this.detectionViewMode();
      const filled = this.detectionFilledPillRef();
      const outlined = this.detectionOutlinedPillRef();
      if (!filled || !outlined) return;
      const View = this.appConfig.Shared.InstantCheckup.Detections.View;
      const target = mode === View.FILLED ? filled : outlined;
      const el = target.nativeElement;
      this.detectionViewIndicatorLeft.set(el.offsetLeft);
      this.detectionViewIndicatorWidth.set(el.offsetWidth);
    });
  }

  get isDravidianScriptUser(): boolean {
    return this.userService.dravidianScripts.includes(this.user?.get('languagePreference'));
  }

  get isHindiUser(): boolean {
    return this.user?.get('languagePreference') === this.appConfig.Shared.Languages.HI;
  }

  async ngOnInit(): Promise<void> {
    this.appWebBridge.setStatusBarColor('cureBlack');
    this.loading = true;
    this.isDocumentRoute = this.route.snapshot.queryParams.mode === 'documents';
    this.isFromListView = this.route.snapshot.queryParams.fromList;
    this.instantCheckupFromSupportChat = this.route.snapshot?.queryParams.className === 'SupportChat';
    this.fromSelfCheckupBanner = this.route.snapshot.queryParams.fromSelfCheckupBanner || this.localStorageService.getValue('fromSelfCheckupBanner');

    this.experiments = await this.connectionService.findUserActiveExperiments();
    await this.experimentService.experimentsLoaded;
    // This concluded experiment is now default-on. Keep the signals for
    // compatibility with older fixtures and the existing view-mode API.
    this.hairAiDetectionTealColor.set(true);
    this.showDetectionViewTab.set(true);
    this.experiments?.forEach(async (experiment: any): Promise<any> => {
      if (experiment.key === 'show_hairline_detection') {
        this.isShowHairlineDetectionExperiment = true;
      }
      if (experiment.key === 'handle_blur_photos') {
        this.handleBlurPhotosExperiment = true;
      }
      if (experiment.key === 'responsive_card_read_more') {
        this.responsiveCardReadMoreExperiment = true;
      }
      if (experiment.key === 'hair_top_with_scores') {
        this.showHairTopWithScores = true;
      }
      if (experiment.key === 'hair_ai_detection_teal_color') {
        this.hairAiDetectionTealColor.set(true);
        this.showDetectionViewTab.set(!!experiment.variant?.showDetectionViewTab);
      }
      if (experiment.key === 'invalid_photo_error_mapping') {
        this.invalidPhotoErrorMappingExperiment = experiment.variant;
      }
    });
    this.excludeInstantCheckupProblems = this.experimentService.getExcludeInstantCheckupProblems();
    await this.loadFollowUpReportStatus();

    this.user = this.connectionService.getActingUser();
    this.currentUser = this.connectionService.getActingUser();
    if (this.fromSelfCheckupBanner) {
      await this.getProgressReportClicked();
    }
    this.subscriptions.push(this.route.params.subscribe(async (param: any): Promise<void> => {
      this.instantCheckupIdFromRoute = param.id;
      await this.getInstantCheckup(this.instantCheckupIdFromRoute);
      this.isReacquisitionUser = this.getIsReacquisitionUser();
      if (this.isReacquisitionUser) this.reAcquisitionService.setIsPhotoTaken(true);
      this.validateAndRenderPhotoStatusBanner();
      this.loading = false;
    }));
  }

  private async loadFollowUpReportStatus(): Promise<void> {
    await this.followUpService.fetchPendingFollowups();
    const pendingFollowUp = this.followUpService.pendingFollowup();
    this.isFollowUpReportPending = pendingFollowUp?.get('State') === this.appConfig.Shared.Followup.State.PENDING
      && pendingFollowUp?.get('ready') === true;
  }

  /**
   * Handles the WebView's own back press, broadcast as NAVIGATION_BACK by app.component.ts.
   * NOT the native camera's back — that Activity reports nothing to the web app (see CLAUDE.md).
   */
  async handleBackPress(): Promise<boolean> {
    this.loading = false;
    this.currentComponent.remove(this);
    // Return false to let app.component.ts handle navigation
    return false;
  }

  getIsReacquisitionUser(): boolean {
    return this.instantCheckup?.extraData?.fromReacquisition === 'true' || this.route.snapshot.queryParams.fromReacquisition === 'true';
  }

  reorderResultsByConcerns(): AiDetectionResult[] {
    const moveMatchingResultsToFront = (concernKeys: string[], results: AiDetectionResult[]): AiDetectionResult[] => {
      const classesToMove = new Set<string>(
        concernKeys.flatMap((key: string): string[] => FACE_PROBLEM_CLASS_MAPPING[key] || []),
      );

      const [movedResults, remainingResults]: [AiDetectionResult[], AiDetectionResult[]] = results.reduce(
        ([moved, remaining]: [AiDetectionResult[], AiDetectionResult[]], result: AiDetectionResult): [AiDetectionResult[], AiDetectionResult[]] => {
          if (classesToMove.has(result.class)
            && !moved.some((r: AiDetectionResult): boolean => r.class === result.class && r.ProblemName === result.ProblemName)) {
            moved.push(result);
          } else {
            remaining.push(result);
          }
          return [moved, remaining];
        },
        [[], []],
      );

      return [...movedResults, ...remainingResults];
    };

    let reorderedResults: AiDetectionResult[] = [...this.results] as AiDetectionResult[];

    if (Array.isArray(this.faceChatSecondaryConcerns) && this.faceChatSecondaryConcerns.length > 0) {
      reorderedResults = moveMatchingResultsToFront(this.faceChatSecondaryConcerns, reorderedResults);
    }

    if (this.faceChatMainConcern && FACE_PROBLEM_CLASS_MAPPING[this.faceChatMainConcern]) {
      reorderedResults = moveMatchingResultsToFront([this.faceChatMainConcern], reorderedResults);
    }

    return reorderedResults;
  }

  private async recordPhotoAnswerForQuiz(): Promise<void> {
    const checkup = this.instantCheckup;
    const questionId = PHOTO_QUESTION_ID_BY_CHECKUP_TYPE[checkup?.type];
    const regimenClass = PHOTO_REGIMEN_CLASS_BY_CHECKUP_TYPE[checkup?.type];
    if (!questionId || !this.isPhotoForCurrentQuiz(regimenClass) || !checkup.unsignedImagePath) return;
    if (!this.experimentService.isFrontendDrivenQuizEnabled(regimenClass)) return;
    const lastBotQuestionId = this.consultationSessionService.getLastBotQuestionId(regimenClass);
    if (!shouldAnswerPhotoQuestion(lastBotQuestionId, questionId)) {
      const requestedQuestionId = PHOTO_QUESTION_ID_BY_CHECKUP_TYPE[checkup.aiOriginalType];
      if (!requestedQuestionId || !shouldAnswerPhotoQuestion(lastBotQuestionId, requestedQuestionId)) return;
    }
    try {
      await this.connectionService.setQuestionResponse(regimenClass as never, questionId, checkup.unsignedImagePath);
    } catch {
    }
  }

  private isPhotoForCurrentQuiz(regimenClass: string): boolean {
    const photoConsultationId = this.instantCheckup?.extraData?.consultationSessionId;
    return !!photoConsultationId
      && photoConsultationId === this.consultationSessionService.getCurrentQuizConsultationId(regimenClass);
  }

  private async recordAcceptedInvalidPhotoAnswer(): Promise<void> {
    if (!this.isInvalidPhoto || !this.canProceedWithInvalidPhoto) return;
    const answerConfig = ACCEPTED_INVALID_PHOTO_ANSWER_BY_TYPE[this.getRequestedPhotoType()];
    const checkup = this.instantCheckup;
    if (!answerConfig || !this.isPhotoForCurrentQuiz(answerConfig.regimenClass) || !checkup.unsignedImagePath) return;
    const lastBotQuestionId = this.consultationSessionService.getLastBotQuestionId(answerConfig.regimenClass);
    const questionId = answerConfig.questionIds.find((id: string): boolean => shouldAnswerPhotoQuestion(lastBotQuestionId, id));
    if (!questionId) return;
    try {
      await this.connectionService.setQuestionResponse(
        answerConfig.regimenClass as never,
        questionId,
        checkup.unsignedImagePath,
      );
    } catch {
    }
  }

  /**
   * Fetch instant checkup and populate landmarks, image, detection etc.
   */

  async getInstantCheckup(idFromRoute: string): Promise<void> {
    this.isHairlineDetected = false;
    this.isTempleBaldnessDetected = false;
    this.isIdealHairlineShown = false;
    this.isFallingHair = false;
    this.hairlineRecessionLobes = [];
    this.scalpParameters = [];
    [this.instantCheckup] = await this.connectionService
      .fetchInstantCheckup({ id: [idFromRoute], includeUnsignedImagePath: true }) ?? [];
    await this.recordPhotoAnswerForQuiz();
    this.isInvalidPhoto = this.instantCheckup.type === ApiClientConstant.InstantCheckup.Type.INVALID;
    this.resolveInvalidPhotoUiCode();
    this.trackInvalidPhotoAttempt();
    this.consultationSession = await this.connectionService.findConsultationSession(this.appConfig.Shared.Regimen.Class.FACE);
    if (this.handleBlurPhotosExperiment) {
      this.checkForBlurryImage(this.instantCheckup);
    }
    this.image = await this.loadImage(this.instantCheckup?.imagePath);
    this.faceLandmarks = this.getLandmarks(this.instantCheckup);
    this.determineIsHairTopType();
    if ((this.isHairTopType || this.isHairFrontType) && this.image) {
      const defaultScale = (this.windowRef.nativeWindow.innerWidth * 0.9) / this.image.width;
      this.croppingConfig = { left: 0, top: 0, ratio: defaultScale, offsetX: 0, offsetY: 0 };
    }

    this.changeDetection.detectChanges();
    // Disable concerns for help trees

    // Add logic here to send the event for invalid photos
    if (this.instantCheckup.type === this.appConfig.Shared.InstantCheckup.Type.INVALID) {
      this.eventLogger.cleverTapEvent('face_not_found', JSON.stringify({ pageName: 'InstantCheckup' }), [
        this.appConfig.Shared.EventTracker.CLEVER_TAP,
        this.appConfig.Shared.EventTracker.MOENGAGE,
        this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
      ]);
    }

    if (this.instantCheckup?.extraData?.className === 'SupportChat' || this.instantCheckup?.extraData?.supportTicketId) {
      this.isHairTopType = false;
      return;
    }

    if (['LEFT_SIDE_FACE', 'FRONT_FACE', 'RIGHT_SIDE_FACE'].includes(this.instantCheckup?.type)) {
      const results = [...(this.instantCheckup?.aiResponse?.result || [])];
      const mainConcern = await this.consultationSession?.get('PrivateMainConcern');

      this.results = [...results
        .filter((result: AiDetectionResult): boolean => result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED)
        .filter((result: AiDetectionResult): boolean =>
          !result.showForGender ||
          this.user?.get('Gender')?.toLowerCase() === result.showForGender?.toLowerCase()
        )
        .filter((result: AiDetectionResult): boolean => !this.excludeInstantCheckupProblems?.includes(result.class))
        .filter((result: AiDetectionResult): boolean =>
          result.class !== 'Melasma' ||
          (
            result.class === 'Melasma' &&
            !['LEFT_SIDE_FACE', 'RIGHT_SIDE_FACE'].includes(this.instantCheckup.type) &&
            (
              (mainConcern && ['pigmentation', 'melasma'].includes(mainConcern.toLowerCase()))
              || (result.Severity && result.Severity?.toLowerCase() !== 'mild')
            )
          )
        ),
      ];

      try {
        /*
         TODO: Instead of using FACE to get the consultation session, we will use the user selected regimen class
         once we have the feature to show analysis result for HAIR as well so that it works for both FACE and HAIR.
       */
        this.faceChatMainConcern = await this.consultationSession?.get('PrivateMainConcern');
        this.faceChatSecondaryConcerns = await this.consultationSession?.get('concernList');
        this.results = this.reorderResultsByConcerns();
      } catch (err) {
        // handle error
      }

      // check severity
      this.results = (this.results as AiDetectionResult[]).map((result: AiDetectionResult): AiDetectionResult => {
        const severity = this.instantCheckupService.getSeverityFromConcern(result);
        return { ...result, severity: typeof severity === 'string' ? severity : undefined };
      });
      this.summaryFullText = (this.results as AiDetectionResult[])
        .map((concern: AiDetectionResult): string => this.problemsCopy[this.user?.get('languagePreference') || 'en'][concern.class]?.name)
        .join(', ');
      this.loading = false;
    } else if (this.isHairFrontType
      && this.instantCheckup?.aiResponse?.result?.length) {
      this.results = [];

      const results = [...(this.instantCheckup?.aiResponse?.result ?? [])]
        .filter((result: AiDetectionResult): boolean => result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED)
        .filter((result: AiDetectionResult): boolean =>
          !result.showForGender ||
          this.user?.get('Gender')?.toLowerCase() === result.showForGender?.toLowerCase()
        );

      const baldness = results.find(result => result?.['class'] === this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS);
      const hairline = results.find(result => result?.['class'] === this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE);

      // falling_hair: the hair is covering the forehead/hairline — the photo is
      // not usable for assessment. Render only the hairline in red and show the
      // falling-hair card with a retake CTA. Skip all temple-baldness / recession
      // / ideal-hairline processing.
      if (this.experimentService.idealHairlineFallingHair && hairline?.falling_hair === true) {
        this.isFallingHair = true;
        this.tag = this.instantCheckup?.originalType || this.instantCheckup?.type;
        if (this.tag === this.appConfig.Shared.InstantCheckup.Type.INVALID) {
          this.tag = this.appConfig.Shared.InstantCheckup.Type.FULL_FACE;
        }
        const hairlinePoints = [...(hairline?.hairline_points ?? [])];
        if (hairlinePoints.length) {
          this.results.push({
            class: this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE,
            PolygonPointsSequences: [{ color: FALLING_HAIR_COLOR, points: hairlinePoints }],
          });
        }
        this.setDefaultHairTopCroppingConfig();
        this.updateHairTopCroppingConfig();
        this.loading = false;
        return;
      }

      const templeBaldnessDetected = !!baldness;

      const hairlinePolygons = this.isShowHairlineDetectionExperiment && hairline
        ? this.buildHairlinePolygons(hairline)
        : null;

      if (templeBaldnessDetected) {
        this.isTempleBaldnessDetected = true;
        if (!this.isIdealHairlineShown) {
          this.results.push({
            class: this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS,
            PolygonPointsSequences: [
              [...(baldness?.ellipse_points?.left ?? [])],
              [...(baldness?.ellipse_points?.right ?? [])],
            ],
          });
        }
      }
      if (this.hairlineRecessionLobes.length) {
        this.results.push({
          class: HAIRLINE_RECESSION_CLASS,
          PolygonPointsSequences: this.hairlineRecessionLobes
            .map((lobe: number[][]): PolygonEntry => ({ points: lobe, outlinedFill: true })),
        });
      }
      const hasDrawableHairlinePolygon = hairlinePolygons?.some((polygon: PolygonEntry): boolean =>
        Array.isArray(polygon) ? polygon.length > 0 : polygon.points?.length > 0,
      );
      if (hasDrawableHairlinePolygon) {
        this.isHairlineDetected = true;
        this.results.push({
          class: this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE,
          PolygonPointsSequences: hairlinePolygons,
        });
      }

      this.setDefaultHairTopCroppingConfig();
      this.updateHairTopCroppingConfig();

    } else {
      this.results = [];

      const hairResults = [...(this.instantCheckup?.aiResponse?.result ?? [])]
        .filter((result: AiDetectionResult): boolean => result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED)
        .filter((result: AiDetectionResult): boolean =>
          !result.showForGender ||
          this.user?.get('Gender')?.toLowerCase() === result.showForGender?.toLowerCase()
        );

      const baldnessResult = hairResults.find(result => result?.['class'] === this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS);
      const hairlineResult = hairResults.find(result => result?.['class'] === this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE);

      const hairlinePolygons = this.isShowHairlineDetectionExperiment && hairlineResult
        ? this.buildHairlinePolygons(hairlineResult)
        : null;

      const hasTopLevelTempleBaldness = this.instantCheckup.aiResponse?.ellipse_points?.left?.length
          || this.instantCheckup.aiResponse?.ellipse_points?.right?.length;
      if (baldnessResult) {
        this.isTempleBaldnessDetected = true;
        if (!this.isIdealHairlineShown) {
          this.results.push({
            class: this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS,
            PolygonPointsSequences: [
              [...(baldnessResult?.ellipse_points?.left ?? [])],
              [...(baldnessResult?.ellipse_points?.right ?? [])],
            ],
          });
        }
      } else if (hasTopLevelTempleBaldness) {
        this.isTempleBaldnessDetected = true;
        this.results.push({
          class: this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS,
          PolygonPointsSequences: [
            [...(this.instantCheckup.aiResponse?.ellipse_points?.left || [])],
            [...(this.instantCheckup.aiResponse?.ellipse_points?.right || [])],
          ],
        });
      }

      if (this.hairlineRecessionLobes.length) {
        this.results.push({
          class: HAIRLINE_RECESSION_CLASS,
          PolygonPointsSequences: this.hairlineRecessionLobes
            .map((lobe: number[][]): PolygonEntry => ({ points: lobe, outlinedFill: true })),
        });
      }

      if (hairlinePolygons?.length) {
        this.isHairlineDetected = true;
        this.results.push({
          class: this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE,
          PolygonPointsSequences: hairlinePolygons,
        });
      } else if (this.isShowHairlineDetectionExperiment && this.instantCheckup.aiResponse?.points_data?.hairline_points?.length) {
        this.isHairlineDetected = true;
        this.results.push({
          class: this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE,
          PolygonPointsSequences: [
            { color: HAIRLINE_WHITE_COLOR, points: (this.instantCheckup.aiResponse?.points_data?.hairline_points || []) },
          ],
        });
      }

      if (this.isHairTopType) {
        this.setDefaultHairTopCroppingConfig();
        this.updateHairTopCroppingConfig();
        this.processScalpDetections();
      }
      this.summaryFullText = this.results
        .map((concern: DisplayDetectionResult): string => this.problemsCopy[this.user?.get('languagePreference') || 'en'][concern.class ?? '']?.name)
        .join(', ');
      this.loading = false;
    }

    if (this.hairAiDetectionTealColor() && !this.showDetectionViewTab() && this.isHairTopType) {
      this.applyTealColorToHairDetections();
    }

    // Move this to before the return statement of support chat
    if (this.instantCheckup?.type === this.appConfig.Shared.InstantCheckup.Type.INVALID) {
      this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
        branch: { name: 'instantCheckupFaceNotDetected' },
        firebase: { name: 'instantCheckupFaceNotDetected' },
      });
    }

    this.updateTagFromInstantCheckup();

    if (!this.isFromListView && this.instantCheckup?.type?.includes('FACE')) {
      const detections = (this.results as AiDetectionResult[]).reduce(
        (prev: Record<string, number>, curr: AiDetectionResult): Record<string, number> => {
          const detectionCount = curr.BoundingBoxes?.length || curr.PolygonPointsSequences?.length || 0;
          const binaryDetectionType = ['Melasma', 'DarkCircle', 'OpenPores'];
          return { ...prev, [curr.class]: binaryDetectionType.includes(curr.class) ? 1 : detectionCount };
        },
        {},
      );

      this.eventLogger.cleverTapEvent('face_concern_detections', JSON.stringify({
        type: this.instantCheckup.type,
        ...detections,
        total_count: this.results?.length || 0,
      }), PRIMARY_CHANNELS);
    }

    if (!this.isFromListView && this.instantCheckup?.type?.includes('HAIR')) {
      const baldnessEllipsePoints = [...(this.instantCheckup?.aiResponse?.result ?? [])]
        .filter((result: AiDetectionResult): boolean => result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED)
        .filter((result: AiDetectionResult): boolean =>
          !result.showForGender ||
          this.user?.get('Gender')?.toLowerCase() === result.showForGender?.toLowerCase()
        ).find((result: AiDetectionResult): boolean => result?.['class'] === this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS)?.ellipse_points
        ?? this.instantCheckup.aiResponse?.ellipse_points;

        this.eventLogger.cleverTapEvent('hair_concern_detections', JSON.stringify({
        type: this.instantCheckup.type,
        hairline: this.isHairlineDetected ? 1 : 0,
        ideal_hairline: this.isIdealHairlineShown ? 1 : 0,
        temple_baldness: Object.keys(baldnessEllipsePoints ?? {})?.length || 0,
        // The ideal-hairline overlay is guidance, not a detection — keep it out of total_count.
        total_count: this.results.reduce(
          (sum: number, result: DisplayDetectionResult): number => sum + (result.PolygonPointsSequences?.length || 0),
          0,
        ) - (this.isIdealHairlineShown ? 1 : 0),
      }));
    }
    this.loading = false;
  }

  validateAndRenderPhotoStatusBanner(): void {
    const isHairTop = this.isHairTopType;
    if (!this.instantCheckup) return;
    if (!isHairTop) return;

    const refreshDate = this.user.get('refreshDate');
    const orderState = this.user.get('orderState') || ApiClientConstant.User.OrderState.UNPAID;
    if (this.instantCheckup.createdAt <= refreshDate) {
      this.instantCheckupStatusBannerState = InstantCheckupStatusBannerStateEnum.PHOTO_REVIEWED;
    } else if ([ApiClientConstant.User.OrderState.UNPAID, ApiClientConstant.User.OrderState.NEW_USER].includes(orderState)) {
      this.instantCheckupStatusBannerState = InstantCheckupStatusBannerStateEnum.PHOTO_WILL_BE_REVIEWED;
    } else {
      this.instantCheckupStatusBannerState = InstantCheckupStatusBannerStateEnum.PHOTO_REVIEW_PENDING;
    }
  }

  private resolveInvalidPhotoUiCode(): void {
    if (!this.invalidPhotoErrorMappingExperiment ||
      !this.isInvalidPhoto) return;

    const errorMessage = this.instantCheckup?.errorMessage;
    if (!errorMessage) return;

    const mappings = this.invalidPhotoErrorMappingExperiment.errorMappings || [];
    const matched = mappings.find((m: InvalidPhotoErrorMapping): boolean => m.errorMessage === errorMessage);
    if (!matched) return;

    const categoryKey = UI_CODE_CATEGORY_MAP[matched.uiCode];
    if (!categoryKey ||
      !this.invalidPhotoErrorMappingExperiment[categoryKey]) return;

    this.resolvedInvalidPhotoUiCode = matched.uiCode as InvalidPhotoUiCode;
  }

  checkForBlurryImage(checkup: any): void {
    this.noBlurDetected = false;
    this.blurWithConcernsDetected = false;
    this.blurWithoutConcernsDetected = false;



    const isFrontFace: boolean = checkup?.type === this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE
      && this.localStorageService.getValue('expectedFacePhoto') === this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE;
    if (!isFrontFace) return;

    const aiResponse = checkup?.aiResponse;
    const isMildMelasma = (c: AiDetectionResult): boolean => c.class !== 'Melasma' || (c.class === 'Melasma' && c.Severity?.toLowerCase() !== 'mild');
    const result: AiDetectionResult[] = aiResponse?.result.filter((result: AiDetectionResult): boolean => isMildMelasma(result));
    const hasConcerns = result.some((concern: AiDetectionResult): boolean => concern?.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED);
    const isBlurry: boolean = aiResponse?.blurClassLabel === this.appConfig.Shared.InstantCheckup.AiResponse.BLURRED;

    // No blur
    if (!isBlurry) {
      this.noBlurDetected = true;
      this.triggerCustomEvent('no_blur');
      return;
    }
    // Blur + concerns
    if (hasConcerns) {
      this.blurWithConcernsDetected = true;
      this.triggerCustomEvent('blur_with_concerns');
      return;
    }
    // Blur + no-concern
    this.blurWithoutConcernsDetected = true;
    this.triggerCustomEvent('blur_without_concerns');
  }

  triggerCustomEvent(eventName: string): void {
    this.eventLogger.cleverTapEvent(`${eventName}`, JSON.stringify({ pageName: 'InstantCheckup' }));
  }

  getLandmarks(instantCheckup: any): { top: Point, left: Point, right: Point, bottom: Point, center: Point, faceBox: any } {

    if (['LEFT_SIDE_FACE', 'FRONT_FACE', 'RIGHT_SIDE_FACE'].includes(instantCheckup?.type)) {
      const landmarks = this.instantCheckup?.aiResponse?.googleResponse?.faceAnnotations?.[0]?.landmarks;
      const distanceBetweenChinAndNose = (landmarks?.CHIN_GNATHION.y || 0) - (landmarks?.NOSE_TIP.y || 0);
      return {
        top: {
          x: landmarks?.FOREHEAD_GLABELLA?.x,
          y: (landmarks?.FOREHEAD_GLABELLA?.y || 0)
            - distanceBetweenChinAndNose,
        },
        left: landmarks?.LEFT_EAR_TRAGION,
        bottom: landmarks?.CHIN_GNATHION,
        right: landmarks?.RIGHT_EAR_TRAGION,
        center: landmarks?.NOSE_TIP,
        faceBox: this.instantCheckup?.aiResponse?.faceBox,
      };
    }

    const landmarks = this.instantCheckup?.aiResponse?.fb_landmarks ?? this.instantCheckup?.aiResponse?.result?.[0]?.fb_landmarks;
    const distanceBetweenChinAndNose = (landmarks?.CHIN_GNATHION?.[1] || 0)
      - (landmarks?.NOSE_TIP?.[1] || 0);
    return {
      top: {
        x: landmarks?.FOREHEAD_GLABELLA?.[0],
        y: (landmarks?.FOREHEAD_GLABELLA?.[1] || 0)
          - distanceBetweenChinAndNose,
      },
      left: {
        x: landmarks?.LEFT_EAR_TRAGION?.[0],
        y: landmarks?.LEFT_EAR_TRAGION?.[1],
      },
      bottom: {
        x: landmarks?.CHIN_GNATHION?.[0],
        y: landmarks?.CHIN_GNATHION?.[1],
      },
      right: {
        x: landmarks?.RIGHT_EAR_TRAGION?.[0],
        y: landmarks?.RIGHT_EAR_TRAGION?.[1],
      },
      center: {
        x: landmarks?.NOSE_TIP?.[0],
        y: landmarks?.NOSE_TIP?.[1],
      },
      faceBox: null,
    };
  }

  /**
   * Select active skin detection
   * @param detection current active detection
   */
  redrawScaledCanvas(zoomBox?: HTMLDivElement): void {
    const overaly = this.windowRef.nativeWindow.document.getElementById('overlayCanvas');
    if (overaly) overaly.parentElement.removeChild(overaly);
    if (zoomBox) zoomBox.removeAttribute('style');
    if (zoomBox) this.findScaling(zoomBox);
  }

  slide(swiper?: SwiperComponent): void {
    const swiperRef = swiper ? swiper.swiperRef : this.swiper?.swiperRef;
    this.readMoreComponents?.forEach((component: ReadMoreComponent): any => component.closeText());
    this.summaryExpanded = false;
    this.expandedItems.fill(false);
    if (!swiperRef) return;

    if (!this.selectedDetection) {
      swiperRef.slideTo(0);
      return;
    }
    const index = this.results.indexOf(this.selectedDetection) + 1;
    swiperRef.slideTo(index);
  }

  selectDetection(event: any): void {
    const [swiperEvent]: any = event;
    if (swiperEvent.activeIndex === 0) {
      this.selectedDetection = null;
    } else {
      this.selectedDetection = this.results[swiperEvent.activeIndex - 1];
    }
  }

  findScaling(zoomBox: HTMLDivElement): void {
    const zoomBoxWidth = zoomBox.offsetWidth;
    const zoomBoxHeight = zoomBox.offsetHeight;
    const zoomBoxAspectRatio = zoomBoxWidth / zoomBoxHeight;
    const zoomBoxCenter = {
      x: zoomBoxWidth / 2,
      y: zoomBoxHeight / 2,
    };
    let points = { minX: Number.MAX_VALUE, minY: Number.MAX_VALUE, maxX: Number.MIN_VALUE, maxY: Number.MIN_VALUE };
    if (this.selectedDetection.BoundingBoxes) points = this.findBoundingBox(this.selectedDetection.BoundingBoxes);
    if (this.selectedDetection.PolygonPointsSequences) points = this.findBoundingPolygon(this.selectedDetection.PolygonPointsSequences as unknown as string[][]);

    points = {
      minX: (points.minX - this.croppingConfig.left) * this.croppingConfig.ratio,
      minY: (points.minY - this.croppingConfig.top) * this.croppingConfig.ratio,
      maxX: (points.maxX - this.croppingConfig.left) * this.croppingConfig.ratio,
      maxY: (points.maxY - this.croppingConfig.top) * this.croppingConfig.ratio,
    };

    let boundingBoxWidth = points.maxX - points.minX;
    let boundingBoxHeight = points.maxY - points.minY;
    const boundingBoxCenter = {
      x: points.minX + boundingBoxWidth / 2,
      y: points.minY + boundingBoxHeight / 2,
    };

    // draw overlay here
    this.drawOverlay(zoomBox, { x: points.minX, y: points.minY }, boundingBoxWidth, boundingBoxHeight);
    // -----------------

    boundingBoxHeight += 100;
    boundingBoxWidth += 100;

    const boundigBoxAspectRatio = boundingBoxWidth / boundingBoxHeight;
    if (boundigBoxAspectRatio > zoomBoxAspectRatio) {
      boundingBoxHeight = (zoomBoxHeight / zoomBoxWidth) * boundingBoxWidth;
    }
    const scale = Math.min((zoomBoxHeight / boundingBoxHeight), 2.5);
    if (scale <= 1) return;

    const x = (zoomBoxCenter.x - boundingBoxCenter.x);
    const y = (zoomBoxCenter.y - boundingBoxCenter.y);

    const maxTranslateX = (zoomBox.clientWidth * scale - zoomBox.clientWidth) / 2;
    const minTranslateX = -(zoomBox.clientWidth * scale - zoomBox.clientWidth) / 2;
    const maxTranslateY = (zoomBox.clientHeight * scale - zoomBox.clientHeight) / 2;
    const minTranslateY = -(zoomBox.clientHeight * scale - zoomBox.clientHeight) / 2;

    const translate = {
      x: Math.sign(x * scale) === 1 ? Math.min(x * scale, maxTranslateX) : Math.max(x * scale, minTranslateX),
      y: Math.sign(y * scale) === 1 ? Math.min(y * scale, maxTranslateY) : Math.max(y * scale, minTranslateY),
    };

    // zoomBox.style.setProperty('transform-origin', `${origin.x}px ${origin.y}px`);
    zoomBox.style.setProperty('translate', `${translate.x}px ${translate.y}px`);
    zoomBox.style.setProperty('transform', `scale(${scale})`);
  }

  findBoundingBox(param: string[]): { minX: number, minY: number, maxX: number, maxY: number } {
    let points = { minX: Number.MAX_VALUE, minY: Number.MAX_VALUE, maxX: Number.MIN_VALUE, maxY: Number.MIN_VALUE };
    param.forEach((pointString: string): void => {
      const pointArray = pointString.split(',').map((x: string): number => +x);
      points = {
        minX: Math.min(pointArray[0], pointArray[2], points.minX),
        minY: Math.min(pointArray[1], pointArray[3], points.minY),
        maxX: Math.max(pointArray[0], pointArray[2], points.maxX),
        maxY: Math.max(pointArray[1], pointArray[3], points.maxY),
      };
    });
    return points;
  }
  findBoundingPolygon(polygons: string[][]): { minX: number, minY: number, maxX: number, maxY: number } {
    let points = { minX: Number.MAX_VALUE, minY: Number.MAX_VALUE, maxX: Number.MIN_VALUE, maxY: Number.MIN_VALUE };
    polygons.forEach((polygon: string[]): void => {
      polygon.forEach((pointString: string): void => {
        const pointArray = pointString.split(',').map((x: string): number => +x);
        points = {
          minX: Math.min(pointArray[0], points.minX),
          minY: Math.min(pointArray[1], points.minY),
          maxX: Math.max(pointArray[0], points.maxX),
          maxY: Math.max(pointArray[1], points.maxY),
        };
      });
    });
    return points;
  }

  drawOverlay(zoomBox: HTMLDivElement, point: { x: number, y: number }, width: number, height: number): void {
    const canvas = this.windowRef.nativeWindow.document.createElement('canvas');
    const canvasContext = canvas.getContext('2d');
    canvas.height = zoomBox.clientHeight;
    canvas.width = zoomBox.clientWidth;
    canvas.id = 'overlayCanvas';
    canvas.style.setProperty('position', 'absolute');
    canvas.style.setProperty('top', '0');
    canvas.style.setProperty('left', '0');
    canvasContext.fillStyle = 'rgba(0, 0, 0, 0.5)';
    canvasContext.fillRect(0, 0, canvas.width, canvas.height);
    canvasContext.stroke();
    canvasContext.beginPath(); // Start a new path
    canvasContext.clearRect(point.x - 10, point.y - 10, width + 20, height + 20);
    canvasContext.rect(point.x - 10, point.y - 10, width + 20, height + 20);
    canvasContext.lineWidth = 3;
    canvasContext.strokeStyle = 'rgba(255, 255, 255, 0.65)';
    canvasContext.stroke();

    zoomBox.appendChild(canvas);
  }

  deleteInstantCheckup(confirmDelete?: boolean): void {
    if (this.ui.deleteLoading || !this.instantCheckup) return;
    this.ui.deleteLoading = true;
    this.connectionService.deleteInstantCheckup(this.instantCheckup.objectId, confirmDelete)
      .then((): void => {
        this.notify('Deleted Successfully');
        this.navigateAfterDelete();
        this.ui.deleteLoading = false;
      })
      .catch((err: any): void => {
        if (err.code === 409) {
          this.bottomSheet.open(DeletePhotoSheetComponent, {
            data: {
              content: err.message || err,
              hideNewPhotoOption: this.isDocumentType,
              clickNewPhotoCallback: (): void => {
                this.goCheckAgain();
              },
              deleteCallback: (): void => {
                this.deleteInstantCheckup(true);
              },
            },
          });
        } else {
          const message = err.message || err;
          this.ui.popUpModal = {
            title: this.appConfig.Shared.String.DELETE_PHOTO,
            open: true,
            okText: this.appConfig.Shared.String.DELETE,
            cancelText: this.appConfig.Shared.String.KEEP_PHOTO,
            message: { text: message },
            type: this.appConfig.Dialog.ALERT,
          };
        }
        this.ui.deleteLoading = false;
      });
  }

  private get shouldOpenMoreInsightsSheetOnBack(): boolean {
    if (!this.fromSelfCheckupBanner) return false;
    if (this.localStorageService.getBooleanValue('moreInsightsBottomsheetShown')) return false;
    const newUiExperimentActive = this.experimentService.isFupQuestionsNewUiEnabled();
    return !this.followUpService.isFirstFollowUp(this.followUp) || !newUiExperimentActive;
  }

  back(step?: number): void {
    if (this.shouldOpenMoreInsightsSheetOnBack) {
      const data = { fromSelfCheckupBanner: this.fromSelfCheckupBanner, followUpId: this.followUp?.id };
      const sheetRef: MatBottomSheetRef<MoreInsightsBottomsheetComponent> = this.bottomSheet.open(MoreInsightsBottomsheetComponent, {data});
      sheetRef.backdropClick().pipe(take(1)).subscribe(() => sheetRef.dismiss());
      this.localStorageService.setValue('moreInsightsBottomsheetShown', true);
      return;
    }

    const cameFromSelfCheckupBanner = this.fromSelfCheckupBanner;
    this.deleteLocalStorageItem('fromSelfCheckupBanner');
    this.deleteLocalStorageItem('moreInsightsBottomsheetShown');
    if (cameFromSelfCheckupBanner) {
      this.navigateToHome();
      return;
    }
    this.broadcast.broadcast('NAVIGATION_BACK', { step });
  }

  navigateToHome(): void {
    this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  private navigateAfterDelete(): void {
    const data = this.appWebBridge.isAppWebBridgeLoaded()
      ? this.instantCheckup?.extraData
      : this.route.snapshot.queryParams;

    const { CONSULTATION_SESSION_CHAT } = this.appConfig.Shared.CLASSNAME;

    if (data?.className === CONSULTATION_SESSION_CHAT || data?.consultationSessionId) {
      this.router.navigate([`/chatV2/${data.consultationSessionId}`], { queryParams: { type: 'consultationSession' } });
      return;
    }

    if (data?.followUpId) {
      this.router.navigate([`/chatV2/${data.followUpId}`], { queryParams: { type: 'followUp' } });
      return;
    }

    if (this.fromSelfCheckupBanner) {
      this.navigateToHome();
      this.deleteLocalStorageItem('fromSelfCheckupBanner');
      return;
    }

    this.back();
  }

  /**
   * Load an image in promise
   * @param src image src
   * @returns HTMLImageElement
   */
  async loadImage(src: string): Promise<HTMLImageElement> {
    return this.instantCheckupService.loadImage(src);
  }

  popUpClosed(result: any): void {
    this.ui.popUpModal = { open: false };
    if (result.clickOnYes) {
      this.deleteInstantCheckup(true);
    }
  }

  notify(message: any): void {
    this.broadcast.broadcast('NOTIFY', { message });
  }

  private trackInvalidPhotoAttempt(): void {
    const requestedPhotoType = this.getRequestedPhotoType();
    if (!requestedPhotoType) {
      return;
    }
    const storageKey = `${INVALID_PHOTO_ATTEMPTS_KEY_PREFIX}${requestedPhotoType}`;
    if (!this.isInvalidPhoto) {
      this.canProceedWithInvalidPhoto = false;
      this.localStorageService.delete(storageKey);
      return;
    }
    const attemptedCheckupIds = this.parseInvalidPhotoAttempts(this.localStorageService.getValue(storageKey));
    const currentCheckupId = this.instantCheckup?.objectId;
    if (currentCheckupId && !attemptedCheckupIds.includes(currentCheckupId)) {
      attemptedCheckupIds.push(currentCheckupId);
      this.localStorageService.setValue(storageKey, JSON.stringify(attemptedCheckupIds));
    }
    this.canProceedWithInvalidPhoto = attemptedCheckupIds.length > INVALID_PHOTO_ATTEMPTS_BEFORE_PROCEED;
  }

  private updateTagFromInstantCheckup(): void {
    const requestedPhotoType = this.getRequestedPhotoTypeFromRecord() || this.instantCheckup?.type;
    this.tag = requestedPhotoType === this.appConfig.Shared.InstantCheckup.Type.INVALID
      ? this.appConfig.Shared.InstantCheckup.Type.FULL_FACE
      : requestedPhotoType;
  }

  private getRequestedPhotoTypeFromRecord(): string {
    return this.instantCheckup?.aiOriginalType || this.instantCheckup?.originalType;
  }

  private getRequestedPhotoType(): string {
    return this.getRequestedPhotoTypeFromRecord()
      || this.localStorageService.getValue('expectedFacePhoto')
      || this.localStorageService.getValue('checkupListExpectedFacePhoto')
      || this.tag;
  }

  private parseInvalidPhotoAttempts(raw: unknown): string[] {
    if (typeof raw !== 'string' || !raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private isHairPhotoType(photoType: string): boolean {
    return !!photoType && photoType.includes(this.appConfig.Shared.InstantCheckup.Type.HAIR);
  }

  get isHairFlowInvalidPhoto(): boolean {
    return this.isInvalidPhoto && this.isHairPhotoType(this.getRequestedPhotoType());
  }

  get shouldShowInvalidPhotoCard(): boolean {
    return this.isInvalidPhoto;
  }

  get hairPhotoNotDetectedCopy(): ScalpCardCopy | undefined {
    return this.concernDetectionsService.getScalpCardCopy({
      state: ScalpCardState.NoDetection,
      gender: this.user?.get('Gender'),
      language: this.user?.get('languagePreference'),
    });
  }

  get isHairFrontInvalidPhoto(): boolean {
    return this.isHairFlowInvalidPhoto
      && this.getRequestedPhotoType() === ApiClientConstant.InstantCheckup.Type.HAIR_FRONT;
  }

  checkAndOpenIncorrectFaceDetectedBottomSheet(): void {
    const expectedPhotoType = this.localStorageService?.getValue('expectedFacePhoto');
    if (!expectedPhotoType) {
      return;
    }
    const instantCheckupPhotoType = this.instantCheckup.type;
    this.doesPhotoTypeMatches = this.isAcceptablePhotoType(instantCheckupPhotoType, expectedPhotoType);
    const isComparablePhotoType = instantCheckupPhotoType.includes(this.appConfig.Shared.InstantCheckup.Type.FACE)
      || this.isHairPhotoType(instantCheckupPhotoType);
    this.shouldShowIncorrectPhotoDetectedSheet = isComparablePhotoType && !this.doesPhotoTypeMatches;
    if (this.shouldShowIncorrectPhotoDetectedSheet) {
      this.bottomSheet.open(WrongPhotoDetectedComponent, {
        data: {
          photoTypes: { expectedPhoto: expectedPhotoType, actualPhoto: instantCheckupPhotoType },
          retakePhotoFromBottomSheet: (): void => {
            this.skipLocalStorageDelete = true;
            this.handleImageCapture();
          },
        },
      });
    }
  }

  /** LEFT/RIGHT/SIDE_FACE are interchangeable — any side photo answers a request for a side photo. */
  private isAcceptablePhotoType(actualType: string, expectedType: string): boolean {
    if (actualType === expectedType || actualType.includes(expectedType)) return true;
    const { SIDE_FACE, LEFT_SIDE_FACE, RIGHT_SIDE_FACE } = this.appConfig.Shared.InstantCheckup.Type;
    const sideFaceTypes: string[] = [SIDE_FACE, LEFT_SIDE_FACE, RIGHT_SIDE_FACE];
    return sideFaceTypes.includes(actualType) && sideFaceTypes.includes(expectedType);
  }

  private parseExtraData(raw: unknown): Record<string, unknown> {
    if (typeof raw !== 'string' || !raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  handleImageCapture(): void {
    const incomingContext: CaptureContext = Object.keys(this.route.snapshot.queryParams ?? {}).length
      ? this.route.snapshot.queryParams
      : parseContext(this.localStorageService.getValue('instantCheckupExtraData'));
    const tag: string = this.localStorageService.getValue('expectedFacePhoto')
      || this.localStorageService.getValue('checkupListExpectedFacePhoto') || this.tag;
    // switches are load-bearing — see photo-capture-service/CLAUDE.md
    this.photoCapture.launch({
      tag,
      intent: CaptureIntent.RETAKE,
      context: mergeContext(this.instantCheckup?.extraData ?? {}, incomingContext),
      persistContext: false,
      logEvents: false,
    });
  }

  private async checkForMissingFacesAndPrompt(): Promise<boolean> {
    const { Shared: { InstantCheckup, Followup }, }: AppConfig = this.appConfig;
    const frontFaceType = InstantCheckup.Type.FRONT_FACE;
    const leftSideType = InstantCheckup.Type.LEFT_SIDE_FACE;
    const rightSideType = InstantCheckup.Type.RIGHT_SIDE_FACE;
    const sideFaceType = InstantCheckup.Type.SIDE_FACE;
    const skipState = Followup.State.SKIP;

    const shouldSkipSideFaceCheck = (
      this.isReacquisitionUser ||
      this.instantCheckupFromSupportChat ||
      this.isHairCheckup ||
      this.instantCheckupService.isSideFaceNudgeShownInSession() ||
      this.localStorageService.getBooleanValue('sideFaceUploadSkipped')
    );
    if (shouldSkipSideFaceCheck) return true;

    const isFrontFace: boolean = this.instantCheckup?.type === frontFaceType
      || this.localStorageService.getValue('expectedFacePhoto') === frontFaceType;

    if (isFrontFace) return true;
    const allInstantCheckups = await this.connectionService.fetchInstantCheckup({ userId: this.currentUser?.id });

    const hasLeftSide = allInstantCheckups?.some((c: any): boolean => c.type === leftSideType);
    const hasRightSide = allInstantCheckups?.some((c: any): boolean => c.type === rightSideType);

    if (hasLeftSide && hasRightSide) return true;

    const availableImagesArray: { image: string; side: string }[] = [];
    let requiredSides = [leftSideType, rightSideType];
    const instantCheckupTypeCreated: string = `${this.instantCheckup?.type?.split('_')[1]}_${this.instantCheckup?.type?.split('_')[2]}`;

    if (this.instantCheckup?.type && (this.instantCheckup?.aiOriginalType === sideFaceType || instantCheckupTypeCreated === sideFaceType)) {
      availableImagesArray.push({
        image: this.instantCheckup.imagePath,
        side: this.instantCheckup.type,
      });
      requiredSides = requiredSides.filter((side: string): boolean => side !== this.instantCheckup.type);
    }
    if (availableImagesArray.length === 2) {
      const leftIndex: number = availableImagesArray.findIndex(
        (item: { image: string; side: string }): boolean => item.side === leftSideType,
      );
      const rightIndex: number = availableImagesArray.findIndex(
        (item: { image: string; side: string }): boolean => item.side === rightSideType,
      );
      if (leftIndex !== -1 && rightIndex !== -1) {
        const leftItem: { image: string; side: string } = availableImagesArray[leftIndex];
        const rightItem: { image: string; side: string } = availableImagesArray[rightIndex];
        availableImagesArray[0] = leftItem;
        availableImagesArray[1] = rightItem;
      }
    }
    this.availableImagesArray = availableImagesArray;
    if (requiredSides.length > 0 && availableImagesArray.length === 1) {
      const bottomSheetRef: any = this.bottomSheet.open(BothSideFaceBottomSheetComponent, {
        data: {
          availableImages: availableImagesArray,
          requiredSide: requiredSides[0],
          callback: async (skipLocalStorageDelete: boolean): Promise<void> => {
            this.skipLocalStorageDelete = skipLocalStorageDelete;
            await this.goCheckAgain();
          },
        },
        disableClose: true,
      });
      bottomSheetRef.backdropClick().subscribe((): void => {
        this.localStorageService.setValue('sideFaceUploadSkipped', true)
        bottomSheetRef.dismiss(skipState);
      });
      const userAction = await bottomSheetRef.afterDismissed().toPromise();
      return userAction === skipState;
    }
    return true;
  }

  private isFromConsultationOrFollowupFlow(): boolean {
    const data = this.appWebBridge.isAppWebBridgeLoaded()
      ? this.instantCheckup?.extraData
      : this.route.snapshot.queryParams;
    const { CONSULTATION_SESSION_CHAT, FOLLOW_UP_CHAT } = this.appConfig.Shared.CLASSNAME;
    const isFromSelfCaptureBanner = data?.fromSelfCaptureBanner === true || data?.fromSelfCaptureBanner === 'true';
    return (
      isFromSelfCaptureBanner || !!data?.fromSelfCheckupBanner ||
      data?.className === CONSULTATION_SESSION_CHAT ||
      data?.className === FOLLOW_UP_CHAT ||
      !!data?.consultationSessionId ||
      !!data?.followUpId
    );
  }

  private isFromAiAssistantFlow(): boolean {
    const data = this.appWebBridge.isAppWebBridgeLoaded()
      ? this.instantCheckup?.extraData
      : this.route.snapshot.queryParams;
    return !!data?.className && data.className === this.appConfig.Shared.CLASSNAME.AI_ASSISTANT_MESSAGE;
  }

  /**
   * This function decides where to take the user to when they click 'continue' button in footer.
   * 1. if no main-concern, then it navigates user to either mainconcern selection page or photo taking page based on experiment.
   * 2. If there is any redirect url set it local-storage, it takes them to there.
   * 3. Else to chat page.
   */
  async continueToNext(): Promise<any> {
    const queryParams = this.route.snapshot.queryParams;
    const sideFaceSheetResult = await this.instantCheckupService.validateAndOpenSideFacePhotoBottomSheet({
      currentInstantCheckup: this.instantCheckup as InstantCheckup,
      isReacquisitionUser: this.isReacquisitionUser,
      isConsultationOrFollowup: this.isFromConsultationOrFollowupFlow(),
      userId: this.currentUser?.id,
      consultationSession: this.consultationSession,
      callback: async (sideFaceQuestionMessageId?: string): Promise<void> => {
        const persistedContext: CaptureContext = this.appWebBridge.isAppWebBridgeLoaded()
          ? this.instantCheckup?.extraData ?? {}
          : parseContext(queryParams.extraData);
        const context: CaptureContext = buildExtraData(
          CaptureIntent.SIDE_FACE_NUDGE,
          mergeContext(queryParams, persistedContext),
        );
        await this.instantCheckupService.navigateToInstantCheckupPreview({
          ...context,
          ...(sideFaceQuestionMessageId ? { messageId: sideFaceQuestionMessageId } : {}),
          tag: EntityEnum.InstantCheckup.Type.SIDE_FACE,
        });
      }
    });
    if (sideFaceSheetResult === SideFacePhotoBottomSheetComponent.DISMISS_UPLOAD) {
      return null;
    }
    const canProceed: boolean = await this.checkForMissingFacesAndPrompt();
    if (!canProceed) {
      return null;
    }
    this.checkAndOpenIncorrectFaceDetectedBottomSheet();
    const openReacquisitionUserBottomSheets = await this.shouldOpenReacquisitionUserBottomSheets();
    if (openReacquisitionUserBottomSheets) {
      return null;
    }
    if (this.shouldShowIncorrectPhotoDetectedSheet) return null;
    await this.recordAcceptedInvalidPhotoAnswer();
    const isFromTakePhotoBanner = this.localStorageService.getBooleanValue('takePhotoBannerClicked');
    if (isFromTakePhotoBanner) {
      this.eventLogger.cleverTapEvent('photo_taken_upload_photo_banner');
      this.localStorageService.delete('takePhotoBannerClicked');
    }
    this.localStorageService.delete('instantCheckupExtraData');
    if (this.isFromAiAssistantFlow()) {
      this.localStorageService.delete('CureSkin/redirectUrl');
      this.aiAssistantService.requestOpen();
      return null;
    }
    const redirectTo = this.localStorageService.getValue('CureSkin/redirectUrl');
    if (redirectTo) return this.connectionService.redirectToLastKnowLocation();
    this.eventLogger.trackEvent('continue_to_chat_from_instant_checkup', { username: this.user.get('username') });
    if (this.user.get('followupTreeTriggered')) {
      const followUp = await this.connectionService.findRecentFollowUp({
        State: [
          this.appConfig.Shared.Followup.State.PENDING,
          this.appConfig.Shared.Followup.State.WAITING_FOR_IMAGE,
        ],
        ready: false,
      });
      return this.router.navigate([`/chatV2/${followUp?.id}`], {
        queryParams: { type: 'followUp' },
      });
    }
    const routeString = await this.checkForRegimenAndNavigate();
    if (routeString) {
      return this.router.navigate(['/user'], { queryParams: { tab: 'regimen', class: routeString } });
    }
    return this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  async checkForRegimenAndNavigate(): Promise<string> {
    const regimenClasses = [this.appConfig.Shared.Regimen.Class.FACE, this.appConfig.Shared.Regimen.Class.HAIR];
    const regimens = await this.connectionService.fetchRegimens(null, true);

    const foundClass = regimenClasses.find((regimenClass: any): boolean => {
      const includesTag = this.tag.includes(regimenClass);
      return includesTag && !this.hasPaidRegimen(regimens, regimenClass);
    });

    return foundClass || '';
  }

  hasPaidRegimen(regimens: Array<AllocatedRegimen>, regimenClass: string): boolean {
    return regimens.some((regimen: AllocatedRegimen): boolean => regimen?.active && regimen?.class === regimenClass);
  }

  /**
  * When Check again button is clicked.
  * 1. Calls native to open camera when its inside native app.
  * 2. Open Native camera through web when experiment is enabled.
  * 3. Routes to capture page, which is a web-cam(video) based capturing experience. We capture the image of video.
  */
  async goCheckAgain(): Promise<any> {
    this.loading = true;
    this.clearLoaderWhenUserReturns();
    this.skipLocalStorageDelete = true;
    this.eventLogger.trackEvent('check_again', { username: this.user.get('username'), type: this.tag });
    this.eventLogger.trackPeopleIncrement({ people_take_photo_clicked: 1 });
    this.handleImageCapture();
  }

  seeHowRetake(): void {
    this.router.navigate(['/user/instantCheckup/preview'], {
      queryParams: { tag: this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT },
    });
  }

  /** Only a cancelled capture returns to the same checkup — see CLAUDE.md. */
  private clearLoaderWhenUserReturns(): void {
    this.captureReturnSubscription?.unsubscribe();
    this.captureReturnSubscription = this.broadcast.on<string>('ACTIVITY_RESUME')
      .pipe(filter((): boolean => this.instantCheckup?.objectId === this.route.snapshot.params.id), take(1))
      .subscribe((): void => { this.loading = false; });
  }

  ngOnDestroy(): void {
    this.captureReturnSubscription?.unsubscribe();
    this.currentComponent.remove(this);
    this.subscriptions.map((each: Subscription): void => each.unsubscribe());
    this.subscriptions = [];
    if (!this.skipLocalStorageDelete) {
      this.deleteLocalStorageItem('expectedFacePhoto');
    }
    this.deleteLocalStorageItem('checkupListExpectedFacePhoto');
  }

  deleteLocalStorageItem(field: string): void {
    this.localStorageService.delete(field);
  }

  async getProgressReportClicked(): Promise<boolean | void> {
    const result = await this.selfCheckupService.resolveProgressFollowUp({ markReady: true });
    if (result) {
      this.followUp = result.followUp;
      if (this.experimentService.isFupQuestionsNewUiEnabled()
          && this.followUp?.id
          && !this.isFollowupQuestionsCompletedForCurrentFollowUp) {
        this.localStorageService.setValue('fupQuestionsActive', this.followUp.id);
      }
    }
  }

  async navigateToFollowUpChat(): Promise<void> {
    if (this.checkAndFireFollowUpQuestionsSheet()) return;
    await this.routeToFollowUpChat();
  }

  private async routeToFollowUpChat(): Promise<void> {
    const followUpId = this.selfCheckupService.getFollowUpId(this.followUp)
      ?? this.localStorageService.getValue('currentProgressFollowUpId');

    if (followUpId) {
      await this.selfCheckupService.navigateToFollowUpChat(followUpId);
      return;
    }
    await this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  checkAndFireFollowUpQuestionsSheet(): boolean {
    if (!this.experimentService.isFupQuestionsNewUiEnabled()) return false;
    if ((this.followUp?.get?.('followUpCount') ?? 0) > 1) return false;
    if (this.fupQuestionsSheetOpened) return true;
    if (!this.followUp) return false;

    this.fupQuestionsSheetOpened = true;
    const data: FollowUpQuestionsSheetData = {
      followUp: this.followUp,
      user: this.connectionService.getActingUser(),
      userImageUrl: this.instantCheckup?.imagePath,
    };
    const sheetRef = this.bottomSheet.open(FollowUpQuestionsSheetComponent, {
      data,
      panelClass: 'fup-questions-sheet-panel',
      hasBackdrop: true,
      disableClose: false,
    });
    sheetRef.afterDismissed().subscribe((result: any): void => {
      this.fupQuestionsSheetOpened = false;
      if (result?.fallbackToChat) {
        this.routeToFollowUpChat();
      } else if (result?.completed) {
        this.deleteLocalStorageItem('fromSelfCheckupBanner');
        this.deleteLocalStorageItem('moreInsightsBottomsheetShown');
        this.deleteLocalStorageItem('fupQuestionsCompleted');
        this.navigateToHome();
      }
    });
    return true;
  }

  get isFollowupQuestionsCompletedForCurrentFollowUp(): boolean {
    const followUpId = this.followUp?.id;
    if (!followUpId) return false;
    return this.localStorageService.getValue('fupQuestionsCompleted') === followUpId;
  }

  navigateToHomeAndClearFollowupFlags(): void {
    this.deleteLocalStorageItem('fromSelfCheckupBanner');
    this.deleteLocalStorageItem('moreInsightsBottomsheetShown');
    this.deleteLocalStorageItem('fupQuestionsCompleted');
    this.navigateToHome();
  }

  async shouldOpenReacquisitionUserBottomSheets(): Promise<boolean> {
    if (!this.isReacquisitionUser) return false;
    const bottomSheets = [
      {
        validation: async (): Promise<boolean> => await this.instantCheckupService.shouldShowRequestPhotoBottomSheet(this.user, this.instantCheckup?.type),
        open: () => this.reAcquisitionService.openRequestPhotoBottomSheet(this.instantCheckup?.type, this.instantCheckup?.imagePath, () => this.goCheckAgain()),
      },
      {
        validation: (): boolean =>  this.reAcquisitionService.getIsPhotoTaken() && !this.reAcquisitionService.getIsPendingCallCreated(),
        open: () => this.reAcquisitionService.openReacquisitionUserQueryBottomSheet(this.user),
      },
      {
        validation: (): boolean => this.reAcquisitionService.getIsPendingCallCreated(),
        open: () => this.reAcquisitionService.openReacquisitionWaitingForCallBottomSheet(this.user),
      }
    ]
    for (const bottomSheet of bottomSheets) {
      const shouldShow = await bottomSheet.validation();
      if (shouldShow) {
        bottomSheet.open();
        return true;
      }
    }
    return false;
  }


  get isHairCheckup(): boolean {
    const Type = ApiClientConstant.InstantCheckup.Type;
    return [Type.HAIR_FRONT, Type.HAIR_TOP].includes(this.instantCheckup?.type);
  }

  get hairTopFaceLandmarks(): any {
    return this.isHairTopType || this.isHairFrontType ? null : this.faceLandmarks;
  }

  get showHairlineMappedCard(): boolean {
    return !this.isFallingHair
      && !this.isTempleBaldnessDetected
      && this.isShowHairlineDetectionExperiment
      && this.isHairlineDetected;
  }

  get hairFrontCardOffsetClass(): string {
    if (this.showHairlineMappedCard) {
      return '-tw-mt-16';
    }
    return this.isDravidianScriptUser ? '-tw-mt-18' : '-tw-mt-20';
  }

  get showLegacyTempleBaldnessCard(): boolean {
    return this.isTempleBaldnessDetected && !this.showHairFrontStateCard;
  }

  get hairFrontCardState(): HairFrontCardState {
    return this.concernDetectionsService.getHairFrontCardState({
      isFilledHairFrontFlow: this.isFilledHairFrontFlow,
      isHairFrontType: this.isHairFrontType || this.showHairFrontFallbackCard,
      isReclassifiedHairFront: this.isReclassifiedHairFront,
      isHairTopInHairFrontFallback: this.showHairFrontFallbackCard,
      isTempleBaldnessDetected: this.isTempleBaldnessDetected,
      isHairlineDetected: this.isHairlineDetected,
    });
  }

  get showHairFrontStateCard(): boolean {
    if (this.isFallingHair) return false;
    return [
      HairFrontCardState.TempleBaldness,
      HairFrontCardState.Reclassified,
      HairFrontCardState.NotVisible,
      HairFrontCardState.Captured,
    ]
      .includes(this.hairFrontCardState);
  }

  get logoImageSrc(): string {
    return this.isHairCheckup
      ? 'https://cdn.cureskin.com/app/img/curehair-logo.png'
      : 'https://cdn.cureskin.com/app/img/cureskin-logo.png';
  }

  get isDocumentType(): boolean {
    return [this.appConfig.Shared.InstantCheckup.Type.REPORT, this.appConfig.Shared.InstantCheckup.Type.DOCUMENT]
      .includes(this.instantCheckup?.type);
  }

  get headerType(): HeaderTypeEnum {
    if (!this.instantCheckup) {
      return HeaderTypeEnum.SKIN_ANALYSIS_RESULT;
    }

    if (this.isDocumentType) {
      return HeaderTypeEnum.DOCUMENT_VIEW;
    }

    const isFacePhoto = [
      ApiClientConstant.InstantCheckup.Type.FRONT_FACE,
      ApiClientConstant.InstantCheckup.Type.SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.LEFT_SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.RIGHT_SIDE_FACE
    ].includes(this.instantCheckup.type);

    if (this.fromSelfCheckupBanner) {
      return HeaderTypeEnum.DATE;
    }

    if (this.isInvalidPhoto) {
      return HeaderTypeEnum.ANALYSIS_RESULT;
    }

    if (this.isHairCheckup || isFacePhoto) {
      return HeaderTypeEnum.CURESKIN_LOGO;
    }

    return HeaderTypeEnum.SKIN_ANALYSIS_RESULT;
  }

  get footerLayout(): FooterLayout {
    // Create a dependency hash
    const currentDeps = `${this.fromSelfCheckupBanner}-${this.handleBlurPhotosExperiment}-${this.isInvalidPhoto}-${this.canProceedWithInvalidPhoto}-${this.blurWithoutConcernsDetected}-${this.blurWithConcernsDetected}-${this.isFollowupQuestionsCompletedForCurrentFollowUp}`;

    // Only recompute if dependencies changed
    if (currentDeps !== this._footerLayoutDependencies) {
      this._footerLayoutDependencies = currentDeps;
      this._footerLayout = this.computeFooterLayout();
    }

    return this._footerLayout;
  }

  private computeFooterLayout(): FooterLayout {
    if (this.fromSelfCheckupBanner) {
      return this.getSelfCheckupBannerLayout();
    }
    if (this.handleBlurPhotosExperiment) {
      return this.getBlurPhotosExperimentLayout();
    }
    return this.getDefaultLayout();
  }


  private getInvalidPhotoLayout(): FooterLayout {
    const retakeButton = {
      variant: ButtonVariant.PRIMARY,
      i18nKey: I18nKey.RE_TAKE_PHOTO,
      action: (): Promise<any> => this.goCheckAgain(),
      shouldShow: true,
      cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
    };
    if (!this.canProceedWithInvalidPhoto) {
      return { buttons: [retakeButton], containerVariant: FooterContainerVariant.DEFAULT };
    }
    return {
      buttons: [
        {
          ...retakeButton,
          variant: ButtonVariant.SECONDARY,
          cssClasses: 'tw-bg-transparent tw-text-teal-600 tw-border tw-border-teal-600 tw-text-500'
        },
        {
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.CONTINUE,
          action: () => this.continueToNext(),
          shouldShow: true,
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }
      ],
      containerVariant: FooterContainerVariant.DEFAULT
    };
  }

  private getSelfCheckupBannerLayout(): FooterLayout {
    if (this.isInvalidPhoto) {
      return this.getInvalidPhotoLayout();
    }

    const primaryButton = this.isFollowupQuestionsCompletedForCurrentFollowUp
      ? {
        variant: ButtonVariant.PRIMARY,
        i18nKey: I18nKey.CONTINUE,
        action: (): void => this.navigateToHomeAndClearFollowupFlags(),
        shouldShow: true,
        // tw-text-500 matches the larger Continue size used by the other
        // footer layouts. Share with Doctor stays at the base size because
        // its longer label needs the extra room within the half-width slot.
        cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500',
      }
      : {
        variant: ButtonVariant.PRIMARY,
        i18nKey: I18nKey.SHARE_WITH_DOCTOR,
        action: (): Promise<void> => this.navigateToFollowUpChat(),
        shouldShow: true,
        cssClasses: 'tw-bg-teal-600 tw-text-white',
      };

    return {
      buttons: [
        {
          variant: ButtonVariant.SECONDARY,
          i18nKey: I18nKey.RE_TAKE_PHOTO,
          action: () => this.goCheckAgain(),
          shouldShow: true,
          cssClasses: 'tw-border tw-border-teal-600 tw-text-teal-600'
        },
        primaryButton,
      ],
      containerVariant: FooterContainerVariant.SELF_CHECKUP
    };
  }

  private getBlurPhotosExperimentLayout(): FooterLayout {
    if (this.blurWithoutConcernsDetected) {
      return this.getBlurWithoutConcernsLayout();
    }
    if (this.blurWithConcernsDetected) {
      return this.getBlurWithConcernsLayout();
    }

    return this.getDefaultLayout();
  }

  private getBlurWithoutConcernsLayout(): FooterLayout {
    if (this.isInvalidPhoto) {
      return this.getInvalidPhotoLayout();
    }

    return {
      buttons: [
        {
          variant: ButtonVariant.SECONDARY,
          i18nKey: I18nKey.CONTINUE,
          action: () => this.continueToNext(),
          shouldShow: true,
          cssClasses: 'tw-bg-transparent tw-text-teal-600 tw-border tw-border-teal-600 tw-text-500'
        },
        {
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.RETAKE,
          action: () => this.goCheckAgain(),
          shouldShow: this.isFaceOrHairWithDetection(),
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }
      ],
      containerVariant: FooterContainerVariant.DEFAULT
    };
  }

  private getBlurWithConcernsLayout(): FooterLayout {
    return {
      buttons: [
        {
          variant: ButtonVariant.SECONDARY,
          i18nKey: I18nKey.RETAKE,
          action: () => this.goCheckAgain(),
          shouldShow: this.isFaceOrHairWithDetection(),
          cssClasses: 'tw-bg-transparent tw-text-teal-600 tw-border tw-border-teal-600 tw-text-500'
        },
        {
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.CONTINUE,
          action: () => this.continueToNext(),
          shouldShow: true,
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }
      ],
      containerVariant: FooterContainerVariant.DEFAULT
    };
  }

  private getDefaultLayout(): FooterLayout {
    if (this.isInvalidPhoto) {
      return this.getInvalidPhotoLayout();
    }

    const textSizeClass = this.user?.get('languagePreference') === this.appConfig.Shared.Languages.TA ? 'tw-text-200' : 'tw-text-500';
    const clampFontSize = this.shouldUseClampedFontSize() ? 'tw-text-[clamp(11px,4vw,18px)]' : '';
    return {
      buttons: [
        {
          variant: ButtonVariant.SECONDARY,
          i18nKey: this.getCheckAgainI18nKey(),
          action: () => this.goCheckAgain(),
          shouldShow: true,
          cssClasses: `tw-bg-transparent tw-text-teal-600 tw-border tw-border-teal-600 ${textSizeClass} ${clampFontSize}`
        },
        {
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.CONTINUE,
          action: () => this.continueToNext(),
          shouldShow: true,
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }
      ],
      containerVariant: FooterContainerVariant.DEFAULT
    };
  }

  private getCheckAgainI18nKey(): I18nKey {
    if (this.isFaceOrHairWithDetection()) {
      return I18nKey.CHECK_AGAIN;
    }
    if (this.isHairWithoutDetection() || this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.INVALID) {
      return I18nKey.RE_TAKE_PHOTO;
    }
    if (this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.REPORT) {
      return I18nKey.CHANGE;
    }
    return I18nKey.RE_TAKE_PHOTO;
  }


  private isFaceOrHairWithDetection(): boolean {
    if (!this.instantCheckup?.type) return false;
    const isFace = this.instantCheckup.type.includes(ApiClientConstant.InstantCheckup.Type.FACE);
    const isHairFrontWithDetection = this.instantCheckup.type === ApiClientConstant.InstantCheckup.Type.HAIR_FRONT &&
      this.instantCheckup?.aiResponse?.hairDetected === true;
    return isFace || isHairFrontWithDetection;
  }


  private isHairWithoutDetection(): boolean {
    if (!this.instantCheckup?.type || !this.instantCheckup?.aiResponse) return false;
    const isHairFrontOrTop = [
      ApiClientConstant.InstantCheckup.Type.HAIR_FRONT,
      ApiClientConstant.InstantCheckup.Type.HAIR_TOP
    ].includes(this.instantCheckup?.type);
    return isHairFrontOrTop && !this.instantCheckup?.aiResponse?.hairDetected;
  }

  private shouldUseClampedFontSize(): boolean {
    return this.isHairWithoutDetection() || this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.INVALID;
  }

  private setDefaultHairTopCroppingConfig(): void {
    if (!this.croppingConfig && this.image) {
      const defaultScale = (this.windowRef.nativeWindow.innerWidth * 0.9) / this.image.width;
      this.croppingConfig = { left: 0, top: 0, ratio: defaultScale, offsetX: 0, offsetY: 0 };
    }
  }

  private updateHairTopCroppingConfig(): void {
    if (!this.croppingConfig || !this.image) return;

    // HAIR_FRONT: position the image so the detection center lands at 30% of
    // canvas height. Computed synchronously (results are already populated) so
    // the image renders at the correct position on the first frame — no jump.
    // The scale MUST match drawImage()'s scale (this.canvas.width / image.width)
    // because the canvas is tw-w-full with no padding — clientWidth equals
    // innerWidth. Using innerWidth * 0.9 here (as the default does) would create
    // a 10% mismatch between croppingConfig.ratio and the real draw scale,
    // shifting detections away from the image.
    if (this.isHairFrontType) {
      const scale = this.windowRef.nativeWindow.innerWidth / this.image.width;
      const canvasHeight = this.windowRef.nativeWindow.innerHeight * 0.6;
      const detectionCenterY = this.computeDetectionCenterY();
      const offsetY = (canvasHeight * 0.30) - (detectionCenterY * scale);
      this.detectionVerticalOffset = offsetY;
      this.croppingConfig = { left: 0, top: 0, ratio: scale, offsetX: 0, offsetY };
      return;
    }

    // HAIR_TOP: refine croppingConfig with the real measured canvas dimensions
    // (for the detection overlay). The image stays centred (detectionVerticalOffset
    // stays null — drawImage uses the centered formula).
    setTimeout((): void => {
      const canvasElement = this.windowRef.nativeWindow.document.querySelector('canvas[appdrawimage]') as HTMLCanvasElement
        || this.windowRef.nativeWindow.document.querySelector('canvas') as HTMLCanvasElement;

      if (!canvasElement || !this.image || canvasElement.clientWidth <= 0 || canvasElement.clientHeight <= 0) {
        return;
      }

      const scale = canvasElement.clientWidth / this.image.width;
      const displayedHeight = this.image.height * scale;
      const offsetY = (canvasElement.clientHeight - displayedHeight) / 2;

      this.croppingConfig = {
        left: 0,
        top: 0,
        ratio: scale,
        offsetX: 0,
        offsetY: offsetY
      };
      this.changeDetection.detectChanges();
    }, 100);
  }

  /**
   * Returns the vertical center (midpoint of min..max y) of all detection points
   * across every result's PolygonPointsSequences, in image-pixel space.
   * Falls back to the image's vertical center when no detection points are found.
   */
  private computeDetectionCenterY(): number {
    let minY = Infinity;
    let maxY = -Infinity;

    for (const result of this.results ?? []) {
      const sequences = result.PolygonPointsSequences;
      if (!sequences) continue;

      for (const sequence of sequences) {
        const points = Array.isArray(sequence) ? sequence : (sequence as { points: number[][] }).points;
        if (!Array.isArray(points)) continue;

        for (const point of points) {
          const y = typeof point === 'number' ? point : point[1];
          if (typeof y === 'number' && !Number.isNaN(y)) {
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
    }

    if (minY === Infinity || maxY === -Infinity) {
      return this.image ? this.image.height / 2 : 0;
    }

    return (minY + maxY) / 2;
  }

  private get aiDetectionResults(): AiDetectionResult[] {
    return [...(this.instantCheckup?.aiResponse?.result || [])];
  }

  private processScalpDetections(): void {
    const normalScalpDetection = this.concernDetectionsService.getNormalScalpDetection(this.aiDetectionResults);
    if (normalScalpDetection) {
      this.scalpDetectionSeverity = ScalpSeverity.Normal;
      this.scalpParameters = this.buildScalpParameters(normalScalpDetection);
      this.pushScalpDetectionResults([normalScalpDetection]);
      return;
    }

    const scalpDetections = this.concernDetectionsService.getScalpSegmentDetections(this.aiDetectionResults);

    if (scalpDetections.length === 0) return;

    this.scalpDetectionSeverity = this.concernDetectionsService.getNormalizedScalpSeverity(scalpDetections[0])
      ?? ScalpSeverity.Unknown;
    this.scalpParameters = this.buildScalpParameters(scalpDetections[0]);
    this.pushScalpDetectionResults(scalpDetections);
  }

  private pushScalpDetectionResults(detections: AiDetectionResult[]): void {
    detections.forEach((detection: AiDetectionResult): void => {
      try {
        const scalpMasks: number[][][] = JSON.parse(detection.scalp_masks || '[]');
        const polygonSequences: number[][][] = scalpMasks.map((polygon: number[][]): number[][] => polygon);

        const scalpResult: AiDetectionResult = {
          ...detection,
          PolygonPointsSequences: polygonSequences,
        };

        this.results.push(scalpResult);
      } catch (error) {
        console.error('Error parsing scalp_masks:', error);
      }
    });
  }

  private buildHairlinePolygons(hairline: AiDetectionResult): PolygonEntry[] {
    const detectedPoints = [...(hairline?.hairline_points ?? [])];
    const idealPoints = [...(hairline?.ideal_hairline ?? [])];
    if (this.isFilledHairFrontFlow) {
      this.isIdealHairlineShown = idealPoints.length > 0;
      if (!this.isIdealHairlineShown) {
        return [{ color: HAIRLINE_DETECTED_COLOR, points: detectedPoints }];
      }
      const { lobes } = computeHairlineRecession(detectedPoints, idealPoints);
      this.hairlineRecessionLobes = lobes;
      if (lobes.length) return [];
      return [{ color: HAIRLINE_DETECTED_COLOR, points: detectedPoints }];
    }
    this.isIdealHairlineShown = this.experimentService.idealHairlineEnabled && idealPoints.length > 0;
    if (!this.isIdealHairlineShown) {
      return [{ color: HAIRLINE_WHITE_COLOR, points: detectedPoints }];
    }
    return [
      { color: HAIRLINE_DETECTED_COLOR, points: detectedPoints },
      { color: HAIRLINE_WHITE_COLOR, points: idealPoints },
    ];
  }

  private applyTealColorToHairDetections(): void {
    this.results = this.results.map((result: DisplayDetectionResult): DisplayDetectionResult => {
      if (!result.PolygonPointsSequences) return result;
      const updatedSequences: PolygonEntry[] = result.PolygonPointsSequences.map(
        (polygon: PolygonEntry): PolygonEntry => {
          if ('points' in polygon) {
            return polygon;
          }
          return { color: TEAL_DETECTION_COLOR, points: polygon };
        },
      );
      return { ...result, PolygonPointsSequences: updatedSequences };
    });
  }

  hasScalpDetections(): boolean {
    if (!this.isHairTopType) {
      return false;
    }
    return this.concernDetectionsService.hasDrawableScalpMasks(this.aiDetectionResults);
  }

  get scalpCardState(): ScalpCardState {
    return this.concernDetectionsService.getScalpCardState({
      isHairTopView: this.isHairTopType,
      results: this.aiDetectionResults,
      hairImageType: this.instantCheckup?.aiResponse?.hairImageType,
    });
  }

  get isFilledHairFrontFlow(): boolean {
    return this.experimentService.idealHairlineEnabled
      && this.experimentService.idealHairlineFilledDetections;
  }

  isScalpFilledView(): boolean {
    return this.hairAiDetectionTealColor()
      && this.showDetectionViewTab()
      && this.isHairTopType
      && this.isFilledViewMode();
  }

  private isFilledViewMode(): boolean {
    return this.detectionViewMode() === this.appConfig.Shared.InstantCheckup.Detections.View.FILLED;
  }

  isDetectionFilled(detection: DisplayDetectionResult): boolean {
    if (this.isHairTopType) return this.isScalpFilledView();
    return detection?.class === HAIRLINE_RECESSION_CLASS && this.isFilledViewMode();
  }

  isDetectionOutlined(): boolean {
    if (this.isFilledHairFrontFlow && this.isHairFrontType) return true;
    if (!this.hairAiDetectionTealColor()) return false;
    if (this.isHairFrontType) return true;
    return this.showDetectionViewTab()
      && this.isHairTopType
      && this.detectionViewMode() === this.appConfig.Shared.InstantCheckup.Detections.View.OUTLINE;
  }

  shouldShowMintGlow(detection: DisplayDetectionResult): boolean {
    if (!this.isDetectionOutlined()) return false;
    const glowExcludedClasses: string[] = [
      this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE,
      HAIRLINE_RECESSION_CLASS,
    ];
    return !glowExcludedClasses.includes(detection?.class);
  }

  getDetectionViewPillClasses(target: DetectionViewMode): Record<string, boolean> {
    const selected = this.detectionViewMode() === target;
    return {
      'tw-text-gray-50': selected,
      'tw-text-gray-900': !selected,
    };
  }

  showDetectionViewToggle(): boolean {
    return !this.isInvalidPhoto && (this.isScalpViewToggleAvailable() || this.isHairFrontViewToggleAvailable());
  }

  private isScalpViewToggleAvailable(): boolean {
    return this.hairAiDetectionTealColor()
      && this.showDetectionViewTab()
      && this.isHairTopType
      && this.hasScalpDetections();
  }

  private isHairFrontViewToggleAvailable(): boolean {
    return this.isFilledHairFrontFlow
      && this.isHairFrontType
      && this.hairlineRecessionLobes.length > 0;
  }

  toggleDetectionView(): void {
    if (!this.showDetectionViewToggle()) return;
    const View = this.appConfig.Shared.InstantCheckup.Detections.View;
    const next = this.detectionViewMode() === View.FILLED ? View.OUTLINE : View.FILLED;
    this.applyDetectionViewMode(next, 'image');
  }

  applyDetectionViewMode(target: DetectionViewMode, source: 'toggle' | 'image'): void {
    if (this.detectionViewMode() === target) return;
    const eventName = this.isHairFrontType ? 'hair_front_detection_toggled' : 'hair_scalp_detection_toggled';
    this.eventLogger.cleverTapEvent(
      'click',
      JSON.stringify({ name: eventName, source, viewMode: target }),
    );
    this.detectionViewMode.set(target);
  }

  determineIsHairTopType(): void {
    const route = this.concernDetectionsService.resolveHairDetectionRoute({
      type: this.instantCheckup?.type,
      aiOriginalType: this.instantCheckup?.aiOriginalType,
      hairImageType: this.instantCheckup?.aiResponse?.hairImageType,
      results: this.aiDetectionResults,
    });
    this.isHairTopType = route.isHairTopType;
    this.isHairFrontType = route.isHairFrontType;
    this.isReclassifiedHairFront = route.isReclassifiedHairFront;
    this.showHairFrontFallbackCard = route.showHairFrontFallbackCard;
  }

  get showFooter(): boolean {
    if (this.blurWithoutConcernsDetected) {
      return false;
    }

    if (this.results?.length) {
      return true;
    }

    return this.showScalpCard || this.showHairFrontCard;
  }

  get showPhotoReviewDisclaimer() {
    return this.instantCheckup && this.instantCheckupStatusBannerState &&
      this.scalpCardState === ScalpCardState.None;
  }

  private buildScalpParameters(scalpDetection: AiDetectionResult): ParameterViewModel[] {
    const parameterInputs = this.concernDetectionsService.getScalpParameterInputs({
      gender: this.user?.get('Gender'),
      detection: scalpDetection,
      language: this.user?.get('languagePreference'),
    });
    return buildAiParameters(parameterInputs ?? []);
  }

  get showScalpCard(): boolean {
    return !this.isInvalidPhoto
      && this.isHairTopType
      && !this.showHairFrontFallbackCard
      && this.scalpCardState !== ScalpCardState.None;
  }

  get showHairFrontCard(): boolean {
    return !this.isInvalidPhoto && (!!this.isHairFrontType || this.showHairFrontFallbackCard) && (
      !!this.showHairFrontStateCard
      || !!this.showLegacyTempleBaldnessCard
      || !!this.showHairlineMappedCard
      || !!this.isFallingHair
    );
  }

  get footerHeightClass(): string {
    if (this.showScalpCard) {
      if (!this.showHairTopWithScores) {
        return 'tw-min-h-[4.5rem]';
      }
      if (this.scalpCardState === ScalpCardState.Normal) {
        return this.showScalpParameterBars ? 'tw-min-h-[9rem]' : 'tw-min-h-[5rem]';
      }
      return 'tw-min-h-[8rem]';
    }
    if (this.showHairFrontCard) {
      if (this.isFallingHair) {
        return 'tw-min-h-[6rem]';
      }
      switch (this.hairFrontCardState) {
        case HairFrontCardState.TempleBaldness:
          return 'tw-min-h-[6rem]';
        case HairFrontCardState.HairlineMapped:
          return 'tw-min-h-[4.5rem]';
        default:
          return 'tw-min-h-[3.5rem]';
      }
    }
    return 'tw-min-h-[3.5rem]';
  }

  get isNoScalpDetectionState(): boolean {
    return this.scalpCardState === ScalpCardState.NoDetection;
  }

  get showScalpParameterBars(): boolean {
    return this.showHairTopWithScores
      && !!this.scalpParameters.length && !this.isNoScalpDetectionState
      && this.scalpCardState !== ScalpCardState.HairFrontDetected;
  }

  get scalpParameterCardCopy(): ScalpCardCopy | undefined {
    return this.concernDetectionsService.getScalpCardCopy({
      state: this.scalpCardState,
      severity: this.scalpDetectionSeverity,
      gender: this.user?.get('Gender'),
      language: this.user?.get('languagePreference'),
    });
  }

  // --- Legacy scalp card getters (used when hair_top_with_scores is off) ---

  get showScalpDetectionSeverity(): boolean {
    return this.scalpCardState === ScalpCardState.Detected
      && this.concernDetectionsService.isScalpLabelSeverity(this.scalpDetectionSeverity);
  }

  get translatedScalpDetectionSeverity(): string | undefined {
    const lang = this.user?.get('languagePreference') || 'en';
    return this.concernSeverityTranslations?.[lang]?.[this.scalpDetectionSeverity];
  }

  get legacyScalpCardTitle(): string | undefined {
    return this.problemsCopy[this.user?.get('languagePreference') || 'en'][SCALP_SEGMENT_CLASS]?.name;
  }

  get scalpCardOffsetClass(): string {
    if (this.showScalpDetectionSeverity) return '-tw-mt-20';
    return this.scalpCardState === ScalpCardState.Detected ? '-tw-mt-15' : '-tw-mt-20';
  }

  get scalpCardHeightClass(): string {
    if (this.showScalpDetectionSeverity) return 'tw-h-fit tw-min-h-34';
    return this.scalpCardState === ScalpCardState.Detected ? 'tw-h-34' : 'tw-h-fit tw-min-h-34';
  }

  get scalpCardDescriptionClampClass(): string {
    if (this.showScalpDetectionSeverity) return 'tw-line-clamp-none';
    return this.scalpCardState === ScalpCardState.Detected ? 'tw-line-clamp-4' : 'tw-line-clamp-none';
  }

  get scalpCardTitleSizeClass(): 'tw-text-400' | 'tw-text-500' {
    const lang = this.user?.get('languagePreference');
    const compactLangs = [
      this.appConfig.Shared.Languages.HI,
      this.appConfig.Shared.Languages.KN,
      this.appConfig.Shared.Languages.TE,
    ];
    return compactLangs.includes(lang) ? 'tw-text-400' : 'tw-text-500';
  }

  get scalpCardDescriptionSizeClass(): 'tw-text-100' | 'tw-text-200' {
    const lang = this.user?.get('languagePreference');
    const compactLangs = [
      this.appConfig.Shared.Languages.HI,
      this.appConfig.Shared.Languages.KN,
      this.appConfig.Shared.Languages.TE,
    ];
    return compactLangs.includes(lang) ? 'tw-text-100' : 'tw-text-200';
  }

  protected readonly ApiClientConstant = ApiClientConstant;
  protected readonly ScalpCardState = ScalpCardState;
}
